import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { ExternalLink } from 'lucide-react';
import { API_ORIGIN } from '../../lib/api';
import { SiteHeader } from '../../components/SiteHeader';
import { SiteFooter } from '../../components/SiteFooter';
import { Card } from '../../components/ui/Card';
import { Pill } from '../../components/ui/Pill';
import { Reveal } from '../../components/landing/Reveal';

interface Incident {
  id: string;
  title: string;
  summary: string | null;
  severity: 'minor' | 'major' | 'critical';
  status: 'investigating' | 'identified' | 'monitoring' | 'resolved';
  postmortem_url: string | null;
  occurred_at: string;
  resolved_at: string | null;
}

const SEVERITY_TONE: Record<Incident['severity'], 'muted' | 'accent' | 'warn'> = {
  minor: 'muted',
  major: 'accent',
  critical: 'warn',
};

export function StatusPage() {
  const { t, i18n } = useTranslation();
  const [health, setHealth] = useState<{ status: string; checks: { database: boolean }; version: string; now: string } | null>(null);
  const [incidents, setIncidents] = useState<Incident[]>([]);

  useEffect(() => {
    axios.get(`${API_ORIGIN}/health`).then((r) => setHealth(r.data))
      .catch(() => setHealth({ status: 'degraded', checks: { database: false }, version: '0.1.0', now: new Date().toISOString() }));
    axios.get<Incident[]>(`${API_ORIGIN}/api/incidents?limit=10`).then((r) => setIncidents(r.data ?? []))
      .catch(() => setIncidents([]));
  }, []);

  const isOk = health?.status === 'ok';

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <SiteHeader />
      <section className="flex-1 max-w-3xl w-full mx-auto px-6 py-12 lg:py-16">
        <Reveal as="div">
          <span className="inline-flex items-center gap-2 rounded-full pill-pearl px-3 py-1 text-[11px] font-medium uppercase tracking-[0.18em]">
            <span className="sparkle-dot" aria-hidden />
            {t('status.eyebrow')}
          </span>
          <h1 className="display text-4xl lg:text-5xl font-semibold tracking-tight mt-4">{t('status.title')}</h1>
        </Reveal>

        <Reveal as="div">
        <Card className="rounded-2xl border-border mt-8 p-6">
          <div className="flex items-center gap-3">
            <span className="relative flex h-3 w-3" aria-hidden>
              <span className={`absolute inline-flex h-full w-full rounded-full opacity-60 ${isOk ? 'bg-emerald-500 motion-safe:animate-ping' : 'bg-warn-500'}`} />
              <span className={`relative inline-flex h-3 w-3 rounded-full ${isOk ? 'bg-emerald-500' : 'bg-warn-500'}`} />
            </span>
            <div className="display text-lg font-medium">
              {isOk ? t('status.allOperational') : t('common.degraded')}
            </div>
          </div>
          <div className="mt-4 text-sm text-muted-foreground num">
            {t('common.version')} {health?.version} · {health ? new Date(health.now).toLocaleString() : '—'}
          </div>
        </Card>
        </Reveal>

        <Reveal as="div" delay={0.06}>
        <Card className="rounded-2xl border-border mt-4 p-6">
          <div className="font-medium">{t('status.subsystems')}</div>
          <ul className="mt-3 text-sm space-y-2">
            <li className="flex justify-between"><span>{t('status.api')}</span><Badge ok={isOk} /></li>
            <li className="flex justify-between"><span>{t('status.database')}</span><Badge ok={!!health?.checks.database} /></li>
          </ul>
        </Card>
        </Reveal>

        <h2 className="display text-2xl font-semibold mt-12 mb-3">{t('status.recentIncidents')}</h2>
        <Card className="rounded-2xl border-border">
          {incidents.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground text-sm">{t('status.noIncidents')}</div>
          ) : (
            <ul className="divide-y divide-border">
              {incidents.map((inc) => (
                <li key={inc.id} className="px-5 py-4">
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="min-w-0">
                      <div className="font-medium">{inc.title}</div>
                      <div className="text-xs text-muted-foreground mt-0.5 num">
                        {new Date(inc.occurred_at).toLocaleString(i18n.language)}
                        {inc.resolved_at && ` — ${t('status.resolvedAt')} ${new Date(inc.resolved_at).toLocaleString(i18n.language)}`}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Pill tone={SEVERITY_TONE[inc.severity]}>{t(`status.severity.${inc.severity}`)}</Pill>
                      <Pill tone={inc.status === 'resolved' ? 'success' : 'accent'}>
                        {t(`status.incidentStatus.${inc.status}`)}
                      </Pill>
                    </div>
                  </div>
                  {inc.summary && (
                    <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{inc.summary}</p>
                  )}
                  {inc.postmortem_url && (
                    <a
                      href={inc.postmortem_url}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 text-xs text-accent-700 dark:text-accent-300 hover:underline underline-offset-4 mt-2 rounded-sm"
                    >
                      {t('status.readPostmortem')} <ExternalLink size={11} />
                    </a>
                  )}
                </li>
              ))}
            </ul>
          )}
        </Card>
        <p className="mt-3 text-xs text-muted-foreground">{t('status.postmortemPolicy')}</p>
      </section>
      <SiteFooter />
    </div>
  );
}

function Badge({ ok }: { ok: boolean }) {
  const { t } = useTranslation();
  return (
    <span className={`text-xs font-medium ${ok ? 'text-emerald-600' : 'text-warn-500'}`}>
      {ok ? t('common.operational') : t('common.degraded')}
    </span>
  );
}
